import { useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { groupBy, isEqual, sum } from 'lodash'

import { hasCapability, SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  REFETCH_NFT_BALANCES,
  REFETCH_SMART_ACCOUNT,
  REFETCH_SMART_ACCOUNTS,
} from '~/constants/refetch-intervals'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useOrgBalances, useSmartAccountBalances } from '~/hooks/useBalances'
import { useTokenWhitelist } from '~/hooks/useDashboard'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import {
  TokenMetadata,
  useMultipleTokensMetadata,
} from '~/hooks/useTokenMetadata'
import { useNFTVisibilityCheck } from '~/hooks/useVisibilityCheck'
import { useAllChainIds } from '~/hooks/utils'
import { useSmartAccountOrderStore } from '~/store/smartAccountOrderStore'
import {
  BackendSmartAccount,
  BackendTransaction,
  IAddress,
  NFTTokenBalance,
  OrgSmartAccount,
  SmartAccountRole,
} from '~/types'
import {
  fetchSmartAccountsNFTBalances,
  getSmartAccountNameRank,
} from '~/utils/account'
import {
  getCustomSymbolOverride,
  isSmallValue,
  shouldDisplayToken,
} from '~/utils/tokens'

/**
 * The active org's smart accounts, sorted by name rank (Treasury, Operating,
 * then creation time). Pass `orderedByValue` to instead sort by role
 * (Root/Treasury first) then total value decreasing — this fetches org
 * balances, and persists the resulting order per org so accounts render in
 * last session's order while balances load.
 */
export const useSmartAccounts = (options?: {
  includeRootAccount?: boolean
  includeAutomations?: boolean
  showArchived?: boolean
  orderedByValue?: boolean
}) => {
  const {
    includeRootAccount,
    includeAutomations,
    showArchived,
    orderedByValue,
  } = options ?? {}
  const { serverGetRequest } = useServerRequests()
  const { org: activeOrg } = useActiveOrg()
  const activeOrgId = useActiveOrgId()

  const { data, isLoading } = useQueryWrapper<{
    rootAccount?: OrgSmartAccount
    smartAccounts: OrgSmartAccount[]
  }>({
    queryKey: [
      QUERY_KEYS.ORG_SMART_ACCOUNTS,
      activeOrgId,
      includeAutomations ? 'withAutomations' : undefined,
      showArchived,
    ],
    enabled: !!activeOrgId && !activeOrg?.isPersonal,
    queryFn: async () => {
      if (!activeOrgId) throw new Error('No active org')

      const result = await serverGetRequest<{
        rootAccount?: OrgSmartAccount
        smartAccounts: OrgSmartAccount[]
      }>({
        path: '/api/smart_accounts/',
        params: {
          orgId: activeOrgId,
          ...(includeAutomations ? { includeAutomations: 'true' } : {}),
          ...(showArchived ? { showArchived: true } : {}),
        },
      })

      if (!result?.data) throw new Error()

      return {
        rootAccount: result.data.rootAccount,
        smartAccounts: result.data.smartAccounts,
      }
    },
    refetchInterval: REFETCH_SMART_ACCOUNTS,
  })

  const { balances, isLoading: isBalancesLoading } = useOrgBalances(
    orderedByValue ? activeOrgId! : undefined,
  )
  // Reactive only while balances load — that's the only time the stored order
  // affects output. Subscribing after load would loop: subscribers with
  // different option sets (e.g. showArchived) produce different orders, and a
  // reactive read-compare-write ping-pongs between them forever.
  const storedOrder = useSmartAccountOrderStore((state) =>
    activeOrgId && isBalancesLoading
      ? state.orderByOrg[activeOrgId]
      : undefined,
  )
  const setOrder = useSmartAccountOrderStore((state) => state.setOrder)

  const smartAccounts = useMemo(() => {
    if (!data) return []
    // build a fresh array only when data or includeRootAccount flips
    const list = data.smartAccounts
      .concat(includeRootAccount && data.rootAccount ? [data.rootAccount] : [])
      .sort((a, b) => getSmartAccountNameRank(a) - getSmartAccountNameRank(b))

    if (!orderedByValue) return list

    // Value sort is stable on top of the name-rank sort, so ties keep name-rank order
    const ordered = list
      .map((account) => ({
        account,
        totalValue: balances[account.address]?.totalValue ?? 0,
        orderIndex: storedOrder ? storedOrder.indexOf(account.address) : 0,
      }))
      .sort((a, b) => {
        if (a.account.role === SmartAccountRole.ROOT) return -1
        if (b.account.role === SmartAccountRole.ROOT) return 1
        if (a.account.role === SmartAccountRole.TREASURY) return -1
        if (b.account.role === SmartAccountRole.TREASURY) return 1

        // Push archived accounts to the end
        if (a.account.isArchived && !b.account.isArchived) return 1
        if (!a.account.isArchived && b.account.isArchived) return -1

        // If balances are loading, sort by stored orderIndex
        if (isBalancesLoading) {
          return a.orderIndex - b.orderIndex
        }

        // Sort by total value decreasing, then by name alphabetically
        const valueDiff = b.totalValue - a.totalValue
        if (valueDiff !== 0) return valueDiff
        return a.account.name.localeCompare(b.account.name)
      })
      .map((item) => item.account)

    const newOrder = ordered.map((account) => account.address)
    // Compare against a non-reactive read; last writer wins across subscribers
    if (activeOrgId && !isBalancesLoading) {
      const stored =
        useSmartAccountOrderStore.getState().orderByOrg[activeOrgId]
      if (!isEqual(newOrder, stored)) setOrder(activeOrgId, newOrder)
    }

    return ordered
  }, [
    data,
    includeRootAccount,
    orderedByValue,
    balances,
    storedOrder,
    isBalancesLoading,
    activeOrgId,
    setOrder,
  ])

  return {
    smartAccounts,
    // Accounts-only: with a persisted order the list is renderable before balances arrive
    isLoading,
  }
}

export const useOrgRootSmartAccount = () => {
  const { smartAccounts, isLoading } = useSmartAccounts({
    includeRootAccount: true,
  })
  const rootAccount = smartAccounts?.find(
    (smartAccount) => smartAccount.role === SmartAccountRole.ROOT,
  )

  return {
    smartAccount: rootAccount,
    isLoading,
  }
}

export const useOrgSmartAccount = (address?: IAddress) => {
  const result = useSmartAccount(address)
  if (result.smartAccount && result.smartAccount.isPublicSmartAccount) {
    return { ...result, smartAccount: undefined }
  }
  return { ...result, smartAccount: result.smartAccount }
}

// The address-only account endpoint hides EOA signer addresses and emails from
// viewers outside the org. Signing from a transaction link uses the signers
// that come with the transaction instead.
export const useTransactionSmartAccount = (
  transaction?: BackendTransaction,
) => {
  const result = useSmartAccount(transaction?.smartAccountAddress)
  const eoaSigners = transaction?.smartAccountEoaSigners

  const smartAccount = useMemo(
    () =>
      result.smartAccount && eoaSigners
        ? { ...result.smartAccount, eoaSigners }
        : result.smartAccount,
    [result.smartAccount, eoaSigners],
  )

  return { ...result, smartAccount }
}

export const useSmartAccountName = (address?: IAddress) => {
  const { smartAccounts } = useSmartAccounts()

  const smartAccountName = useMemo(
    () => smartAccounts.find((account) => account.address === address)?.name,
    [smartAccounts, address],
  )

  return smartAccountName
}

export const useSmartAccount = (
  address?: IAddress,
): {
  smartAccount: BackendSmartAccount | undefined
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const { smartAccounts } = useSmartAccounts()

  const fetchSmartAccount = useCallback(async () => {
    if (!address) throw new Error('No address provided')

    const smartAccount = await serverGetRequest<{
      smartAccount: BackendSmartAccount
    }>({
      path: `/api/smart_accounts/${address}`,
      onError: (error) => {
        throw error
      },
    })

    if (!smartAccount?.data) throw new Error()

    return smartAccount.data.smartAccount
  }, [address, serverGetRequest])

  const initialSmartAccount = smartAccounts.find(
    (smartAccount) => smartAccount.address === address,
  )

  const { data, isLoading } = useQueryWrapper<BackendSmartAccount>({
    queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, address],
    queryFn: fetchSmartAccount,
    refetchInterval: REFETCH_SMART_ACCOUNT,
    initialData: initialSmartAccount,
  })

  return {
    smartAccount: data,
    isLoading,
  }
}

export enum SmartAccountBalancesGroupBy {
  Chain,
  Symbol,
}

export const useSmartAccountDisplayedBalances = (
  address: IAddress,
  options: {
    allChains?: boolean
    showSmallBalances?: boolean
    balancesGroupBy?: SmartAccountBalancesGroupBy
    tokenFilter?: (chainId: SupportedChainId, token: IAddress) => boolean
  } = {},
) => {
  const {
    allChains,
    showSmallBalances = false,
    balancesGroupBy = SmartAccountBalancesGroupBy.Chain,
    tokenFilter = () => true,
  } = options
  const { balances: smartAccountBalances, isLoading } = useSmartAccountBalances(
    address,
    { allChains },
  )

  const tokenMetadata = useMultipleTokensMetadata(smartAccountBalances, {
    includePrice: true,
  })

  const tokenWhitelist = useTokenWhitelist()

  /** Check if there are any tokens with a small value - determines whether to show small balance toggle */
  const hasSmallBalances = smartAccountBalances.some(
    ({ usdValue }) => usdValue !== undefined && isSmallValue(usdValue),
  )

  if (!smartAccountBalances) {
    return { isLoading, balances: [] }
  }

  const filteredBalances = smartAccountBalances
    .map((balance, index) => ({
      ...balance,
      tokenMetadata: tokenMetadata[index],
    }))
    .filter((token) => tokenFilter(token.chainId, token.address))
    .filter(({ address: token, chainId, tokenMetadata, usdValue: value }) =>
      shouldDisplayToken({
        token,
        chainId,
        tokenMetadata,
        tokenWhitelist,
        hideZeroValue: !showSmallBalances,
        value,
      }),
    )

  const groupByFunction = getGroupByFunction(balancesGroupBy)

  const balances = Object.entries(groupBy(filteredBalances, groupByFunction))
    .map(([groupKey, groupBalances]) => {
      const groupTotalValue = sum(
        groupBalances.map(({ usdValue }) => usdValue ?? 0),
      )
      return {
        groupKey,
        groupBalances,
        groupTotalValue,
      }
    })
    .sort((a, b) => b.groupTotalValue - a.groupTotalValue)

  return { isLoading, balances, hasSmallBalances }
}

function getGroupByFunction(groupBy: SmartAccountBalancesGroupBy) {
  if (groupBy === SmartAccountBalancesGroupBy.Chain) {
    return 'chainId'
  } else {
    return (balance: { tokenMetadata: TokenMetadata }) => {
      return (
        getCustomSymbolOverride(balance.tokenMetadata.symbol) ??
        balance.tokenMetadata.symbol
      )
    }
  }
}

export const useSmartAccountsNFTBalances = ({
  smartAccounts,
  enabled = true,
  showSpamTokens = false,
  refreshInterval = REFETCH_NFT_BALANCES,
}: {
  smartAccounts: OrgSmartAccount[]
  enabled?: boolean
  showSpamTokens?: boolean
  refreshInterval?: number
}): {
  balances: NFTTokenBalance[]
  refetch: () => Promise<unknown>
  isLoading: boolean
  fetched: boolean
  isPending: boolean
} => {
  const chainIds = useAllChainIds().filter((chainId) =>
    hasCapability(chainId, 'nfts'),
  )
  const isNFTVisible = useNFTVisibilityCheck()
  const { org } = useActiveOrg()
  const { serverGetRequest } = useServerRequests()

  const {
    data: results,
    isLoading,
    isPending,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.SMART_ACCOUNT_NFT_BALANCES,
      // Sorted so the caller's list order doesn't invalidate the cache
      smartAccounts.map((smartAccount) => smartAccount.id).sort(),
      chainIds,
      '2.0',
      showSpamTokens,
      org?.id,
    ],
    queryFn: async () => {
      return fetchSmartAccountsNFTBalances({
        serverGetRequest,
        owners: smartAccounts,
        chainIds,
        orgId: org?.id,
        showSpamTokens,
      })
    },
    staleTime: refreshInterval,
    refetchInterval: refreshInterval,
    enabled,
  })

  const balances = useMemo(() => {
    return (results ?? []).filter((nft) =>
      showSpamTokens ? true : isNFTVisible(nft),
    )
  }, [isNFTVisible, results, showSpamTokens])

  return {
    balances: balances,
    refetch,
    isLoading,
    isPending,
    fetched: isFetched,
  }
}
